import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWishlist, removeFromWishlist } from '../Redux/Slice/wishlist.slice';
import { addToCart as addToCartAction, fetchCart } from '../Redux/Slice/cart.slice';
import { IMAGE_URL } from '../Utils/baseUrl';
import { useNavigate } from 'react-router-dom';
import BackgroundColor from '../component/BackgroundColor';
import { FiTrash2, FiShoppingCart, FiPlay } from 'react-icons/fi';
import { FaHeart, FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiNintendoswitch, SiPlaystation, SiOculus, SiSteamdeck } from 'react-icons/si';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import WishlistSkeleton from '../component/skeleton-loaders/WishlistSkeleton';

export default function Wishlist() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading } = useSelector((state) => state.wishlist);
  const cartItems = useSelector((state) => state.cart?.items || []);
  const userId = localStorage.getItem('yoyouserId');
  const token = localStorage.getItem('yoyoToken');

  useEffect(() => {
    document.title = 'Wishlist • YOYO';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!(userId && token)) {
      navigate('/login');
      return;
    }
    if (userId) {
      dispatch(fetchWishlist());
    }
  }, [dispatch, userId, token, navigate]);

  const handleRemoveFromWishlist = (gameId) => {
    dispatch(removeFromWishlist({ gameId }));
  };

  const handleAddToCart = (game, platform) => {
    const gameId = game._id;
    const platformData = game.platforms[platform];
    if (platformData && platformData.available) {
      dispatch(addToCartAction({
        gameId,
        platform,
        qty: 1
      }));
    }
  };

  const isGameInCart = (gameId, platform) => {
    return cartItems.some(item =>
      String(item.game?._id || item.game) === String(gameId) &&
      item.platform === platform
    );
  };

  const getBestPrice = (game) => {
    const platforms = game.platforms;
    if (!platforms) return 'Free';

    const prices = [];
    Object.keys(platforms).forEach(platform => {
      if (platforms[platform].available && platforms[platform].price > 0) {
        prices.push(platforms[platform].price);
      }
    });
    if (prices.length === 0) return 'Free';
    return prices.length > 0 ? Math.min(...prices) : 'Free';
  };

  const getAvailablePlatforms = (game) => {
    const platforms = game.platforms;
    if (!platforms) return {};

    const availablePlatforms = {};
    if (platforms.windows?.available) {
      availablePlatforms.windows = platforms.windows;
    }
    if (platforms.ios?.available) {
      availablePlatforms.ios = platforms.ios;
    }
    if (platforms.android?.available) {
      availablePlatforms.android = platforms.android;
    }
    if (platforms.nintendo?.available) {
      availablePlatforms.nintendo = platforms.nintendo;
    }
    if (platforms.quest?.available) {
      availablePlatforms.quest = platforms.quest;
    }
    if (platforms.playstation?.available) {
      availablePlatforms.playstation = platforms.playstation;
    }
    if (platforms.xbox?.available) {
      availablePlatforms.xbox = platforms.xbox;
    }
    if (platforms.visionpro?.available) {
      availablePlatforms.visionpro = platforms.visionpro;
    }
    if (platforms.steamdeck?.available) {
      availablePlatforms.steamdeck = platforms.steamdeck;
    }

    return availablePlatforms;
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'windows':
        return <FaWindows className="text-blue-500" />;
      case 'ios':
        return <FaApple className="text-white" />;
      case 'android':
        return <DiAndroid className="text-green-500" />;
      case 'nintendo':
        return <SiNintendoswitch className="text-red-500" />;
      case 'quest':
        return <SiOculus className="text-sky-400" />;
      case 'playstation':
        return <SiPlaystation className="text-blue-500" />;
      case 'xbox':
        return <FaXbox className="text-green-500" />;
      case 'visionpro':
        return <TbDeviceVisionProFilled className="text-purple-400" />;
      case 'steamdeck':
        return <SiSteamdeck className="text-blue-400" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <BackgroundColor className="relative overflow-x-hidden">
        <WishlistSkeleton />
      </BackgroundColor>
    );
  }

  // If no data, show empty state
  if (!items || items.length === 0) {
    return (
      <BackgroundColor className="relative overflow-x-hidden">
        <div className="pt-20 sm:pt-24 pb-12 sm:pb-16 w-full max-w-[1280px] mx-auto px-3 sm:px-4">
          <div className="relative mb-6 sm:mb-8">
            <h1 className="text-[20px] sm:text-[24px] md:text-[36px] lg:text-[44px] font-extrabold tracking-wide text-white">
              Your Wishlist
            </h1>
            <p className="text-white/60 mt-1 text-sm sm:text-base">Your favorite games saved for later</p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-6 sm:p-10 text-center flex flex-col items-center justify-center min-h-[400px] sm:min-h-[500px]">
            <FaHeart className="w-12 h-12 sm:w-16 sm:h-16 text-white/30 mx-auto mb-4" />
            <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-2">Your wishlist is empty</div>
            <div className="text-white/60 mb-6 text-sm sm:text-base">Start adding games to your wishlist to see them here.</div>
            <button
              onClick={() => navigate('/games')}
              className="px-4 sm:px-6 py-2 sm:py-3 rounded-md bg-gradient-primary transition-all text-sm sm:text-base"
            >
              Explore Games
            </button>
          </div>
        </div>
      </BackgroundColor>
    );
  }

  return (
    <BackgroundColor className="relative overflow-x-hidden">
      <div className="pt-20 sm:pt-24 pb-12 sm:pb-16 w-full max-w-[1280px] mx-auto px-3 sm:px-4">
        <div className="relative mb-6 sm:mb-8">
          <h1 className="text-[20px] sm:text-[24px] md:text-[36px] lg:text-[44px] font-extrabold tracking-wide text-white">
            Your Wishlist
          </h1>
          <p className="text-white/60 mt-1 text-sm sm:text-base">Your favorite games saved for later</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Items */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4 min-w-0">
            {items.map((item) => {
              const game = item.game;
              const gameId = game._id;
              const availablePlatforms = getAvailablePlatforms(game);
              const bestPrice = getBestPrice(game);
              const isNewGame = new Date(game.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

              return (
                <div
                  key={gameId}
                  className="group bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 flex gap-3 sm:gap-4 hover:border-purple-400/40 transition-colors w-full overflow-hidden"
                >
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 overflow-hidden rounded-lg sm:rounded-xl shrink-0">
                    <img
                      src={game.cover_image?.url || `${IMAGE_URL}placeholder.png`}
                      alt={game.title || 'Game'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {isNewGame && (
                      <span className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-bold">
                        NEW
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button
                        onClick={() => navigate(`/gamedetails/${gameId}`)}
                        className="bg-white/90 text-gray-900 p-2 sm:p-3 rounded-full hover:bg-white hover:scale-110 transition-all duration-300 shadow-2xl"
                      >
                        <FiPlay className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 sm:gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold truncate text-sm sm:text-base md:text-lg">
                          {game.title || 'Untitled'}
                        </div>
                        <div className="mt-1 text-white/60 text-xs sm:text-sm line-clamp-2">
                          {game.description || 'No description available'}
                        </div>

                        {/* Platform Selection Buttons */}
                        {Object.keys(availablePlatforms).length > 0 && (
                          <div className="space-y-2 mt-2 sm:mt-3">
                            <div className="text-xs text-white/60 mb-1 sm:mb-2">Select Platform</div>
                            <div className="flex flex-col md600:flex-wrap md600:flex-row gap-1.5 md600:gap-2">
                              {Object.keys(availablePlatforms).map(platform => {
                                const platformData = availablePlatforms[platform];
                                const isInCart = isGameInCart(gameId, platform);

                                return (
                                  <button
                                    key={platform}
                                    onClick={() => handleAddToCart(game, platform)}
                                    disabled={isInCart}
                                    className={`flex-1 p-1.5 sm:p-2 rounded-lg flex items-center justify-between transition-all duration-300 text-xs sm:text-sm ${isInCart
                                      ? 'bg-green-500/20 border-2 border-green-500/40 text-green-400 cursor-not-allowed'
                                      : 'bg-white/5 hover:bg-purple-500/20 border-2 border-white/20 hover:border-purple-500/40 text-white hover:text-purple-300 hover:scale-105'
                                      }`}
                                  >
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                      <div className="text-sm sm:text-lg">
                                        {getPlatformIcon(platform)}
                                      </div>
                                      <span className="capitalize font-medium">{platform}</span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-xs sm:text-sm font-bold">
                                        ${platformData.price || '0.00'}
                                      </div>
                                      {isInCart && (
                                        <div className="text-xs text-green-400 mt-0.5 sm:mt-1">✓ In Cart</div>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 sm:gap-2 items-end shrink-0">
                        <button
                          onClick={() => handleRemoveFromWishlist(gameId)}
                          className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-white/80 hover:text-red-400 transition-colors"
                          aria-label="Remove from wishlist"
                        >
                          <FiTrash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="h-fit lg:sticky lg:top-24">
            <div className="bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:pb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm sm:text-base text-white/80">Total Items</span>
                <span className="text-sm sm:text-base text-white font-semibold">{items.length}</span>
              </div>
              <div className='flex flex-col sm:flex-row lg:flex-col items-center justify-between gap-2 lg:gap-0'>
                <button onClick={() => navigate('/games')} className="w-full py-2 lg:py-3 rounded-md bg-gradient-primary text-white font-semibold shadow-lg transition-all text-sm md:text-base">Explore More Games</button>
                <button onClick={() => navigate('/cart')} className="w-full py-2 mt-0 lg:mt-2 rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors text-sm md:text-base">View Cart</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BackgroundColor>
  );
}
